import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { CardsApi } from '@shared/api/rest';
import {
  CardStatus,
  type CardDetailsResponse,
  type RemindPinRequest,
  type ReportLostStolenRequest,
  type UpdateCategoryCardRequest,
  type UpdateLabelCardRequest,
} from '@shared/api/rest/cards';

import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getCardEffect = createEffect((params: IQueryParams<number>) => CardsApi.getCard(params.request, params?.signal));
const freezeCardEffect = createEffect((params: IQueryParams<number>) => CardsApi.freezeCard(params?.request, params?.signal));
const unfreezeCardEffect = createEffect((params: IQueryParams<number>) => CardsApi.unFreezeCard(params.request, params?.signal));
const remindPinEffect = createEffect((params: IQueryParams<RemindPinRequest>) => CardsApi.remindPin(params.request, params?.signal));
const updateLabelCardEffect = createEffect((params: IQueryParams<UpdateLabelCardRequest>) =>
  CardsApi.updateLabelCard(params.request, params?.signal),
);
const updateCategoryCardEffect = createEffect((params: IQueryParams<UpdateCategoryCardRequest>) =>
  CardsApi.updateCategoryCard(params.request, params?.signal),
);
const reportLostStolenEffect = createEffect((params: IQueryParams<ReportLostStolenRequest>) =>
  CardsApi.reportLostStolen(params.request, params?.signal),
);
const activateEffect = createEffect((params: IQueryParams<number>) => CardsApi.activate(params.request, params?.signal));

const clearEvent = createEvent();

const $dataStore = createStore<CardDetailsResponse | null>(null)
  .on(getCardEffect.done, (current, data) => data.result.data || current)
  .on(freezeCardEffect.done, (current, data) => (data.result.data ? { ...current!, status: data.result.data.status } : current))
  .on(unfreezeCardEffect.done, (current, data) => (data.result.data ? { ...current!, status: data.result.data.status } : current))
  .on(updateLabelCardEffect.done, (current, data) => data.result.data || current)
  .on(updateCategoryCardEffect.done, (current, data) => data.result.data || current)
  .on(activateEffect.done, (current, data) => (data.result.data ? { ...current!, status: CardStatus.ACTIVE } : current))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardEffect.pending, (_, data) => data)
  .on(getCardEffect.fail, (_, data) => false)
  .on(getCardEffect.done, (_, data) => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardEffect, () => null)
  .on(getCardEffect.fail, (_, data) => data.error.message)
  .on(getCardEffect.done, (_, data) => null)
  .on(freezeCardEffect, () => null)
  .on(freezeCardEffect.fail, (_, data) => data.error.message)
  .on(freezeCardEffect.done, (_, data) => null)
  .on(unfreezeCardEffect, () => null)
  .on(unfreezeCardEffect.fail, (_, data) => data.error.message)
  .on(unfreezeCardEffect.done, (_, data) => null)
  .on(remindPinEffect, () => null)
  .on(remindPinEffect.fail, (_, data) => data.error.message)
  .on(remindPinEffect.done, (_, data) => null)
  .on(updateLabelCardEffect, () => null)
  .on(updateLabelCardEffect.fail, (_, data) => data.error.message)
  .on(updateLabelCardEffect.done, (_, data) => null)
  .on(updateCategoryCardEffect, () => null)
  .on(updateCategoryCardEffect.fail, (_, data) => data.error.message)
  .on(updateCategoryCardEffect.done, (_, data) => null)
  .on(reportLostStolenEffect, () => null)
  .on(reportLostStolenEffect.fail, (_, data) => data.error.message)
  .on(reportLostStolenEffect.done, (_, data) => null)
  .on(activateEffect, () => null)
  .on(activateEffect.fail, (_, data) => data.error.message)
  .on(activateEffect.done, (_, data) => null)
  .reset(clearEvent);
$errorStore.watch((x) => ApiUtils.errorStoreActionForbiddenWatcher(x, $notificationStore.events.setError));

export const $cardStore = {
  events: {
    getCard: ApiUtils.wrapperApi(getCardEffect, RequestStrategyEnum.Debounce),
    freezeCard: freezeCardEffect,
    unfreezeCard: unfreezeCardEffect,
    remindPin: remindPinEffect,
    updateLabelCard: updateLabelCardEffect,
    updateCategoryCard: updateCategoryCardEffect,
    reportLostStolen: reportLostStolenEffect,
    activate: activateEffect,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
